import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Camera, Move, RotateCcw, ZoomIn, ZoomOut } from 'lucide-react'

import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog'

// ─── PTZ helpers ─────────────────────────────────────────────────────────
// pan/tilt/zoom are not in lib.dom's MediaTrack* types yet, so the track is
// read through a loose shape and constraints are applied via `advanced`.

interface PtzRange {
  min: number
  max: number
  step: number
}

type PtzAxis = 'pan' | 'tilt' | 'zoom'

type PtzRanges = Partial<Record<PtzAxis, PtzRange>>
type PtzValues = Partial<Record<PtzAxis, number>>

const PTZ_AXES: PtzAxis[] = ['pan', 'tilt', 'zoom']

const AXIS_LABEL: Record<PtzAxis, string> = {
  pan: 'Pan',
  tilt: 'Tilt',
  zoom: 'Optical zoom',
}

const readPtzRanges = (track: MediaStreamTrack): PtzRanges => {
  const caps: any =
    typeof track.getCapabilities === 'function' ? track.getCapabilities() : {}
  const ranges: PtzRanges = {}

  PTZ_AXES.forEach((axis) => {
    const range = caps?.[axis]
    // A camera that reports the axis but cannot actually move it comes back
    // with min === max; treat that as unsupported.
    if (
      range &&
      typeof range.min === 'number' &&
      typeof range.max === 'number' &&
      range.max > range.min
    ) {
      ranges[axis] = {
        min: range.min,
        max: range.max,
        step: typeof range.step === 'number' && range.step > 0 ? range.step : 1,
      }
    }
  })

  return ranges
}

const readPtzValues = (track: MediaStreamTrack, ranges: PtzRanges): PtzValues => {
  const settings: any =
    typeof track.getSettings === 'function' ? track.getSettings() : {}
  const values: PtzValues = {}

  PTZ_AXES.forEach((axis) => {
    const range = ranges[axis]
    if (!range) return
    values[axis] =
      typeof settings?.[axis] === 'number'
        ? settings[axis]
        : (range.min + range.max) / 2
  })

  return values
}

// ─── Digital pan/zoom helpers ────────────────────────────────────────────

const CONTAINER_ASPECT = 4 / 3
const MIN_DIGITAL_ZOOM = 1
const MAX_DIGITAL_ZOOM = 4
const OUTPUT_WIDTH = 640
const OUTPUT_HEIGHT = 480

interface Center {
  x: number
  y: number
}

const DEFAULT_CENTER: Center = { x: 0.5, y: 0.5 }

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value))

/**
 * Keeps the visible crop inside the frame. At zoom z the crop is 1/z of the
 * frame, so its centre can only travel between 0.5/z and 1 - 0.5/z.
 */
const clampCenter = (center: Center, zoom: number): Center => {
  const margin = 0.5 / zoom
  return {
    x: clamp(center.x, margin, 1 - margin),
    y: clamp(center.y, margin, 1 - margin),
  }
}

// ─── Component ───────────────────────────────────────────────────────────

interface CameraCaptureDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSnap: (dataUrl: string) => void
}

export const CameraCaptureDialog: React.FC<CameraCaptureDialogProps> = ({
  open,
  onOpenChange,
  onSnap,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const frameRef = useRef<HTMLDivElement>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const trackRef = useRef<MediaStreamTrack | null>(null)

  const [error, setError] = useState('')
  const [ptzRanges, setPtzRanges] = useState<PtzRanges>({})
  const [ptzValues, setPtzValues] = useState<PtzValues>({})
  const ptzDefaultsRef = useRef<PtzValues>({})

  const [zoom, setZoom] = useState(MIN_DIGITAL_ZOOM)
  const [center, setCenter] = useState<Center>(DEFAULT_CENTER)
  const [dragging, setDragging] = useState(false)
  const dragOriginRef = useRef<{ x: number; y: number; center: Center } | null>(
    null
  )

  const hasPtz = PTZ_AXES.some((axis) => !!ptzRanges[axis])

  // applyConstraints rejects if it is called while a previous call is still in
  // flight, so PTZ writes are coalesced into a single drain loop.
  const ptzPendingRef = useRef<PtzValues | null>(null)
  const ptzApplyingRef = useRef(false)

  const drainPtz = useCallback(async () => {
    if (ptzApplyingRef.current) return
    ptzApplyingRef.current = true
    try {
      while (ptzPendingRef.current) {
        const next = ptzPendingRef.current
        ptzPendingRef.current = null
        const track = trackRef.current
        if (!track) break
        try {
          await (track as any).applyConstraints({ advanced: [next] })
        } catch {
          // The camera refused this position (out of range or busy) — keep the
          // stream alive and let the next slider move try again.
        }
      }
    } finally {
      ptzApplyingRef.current = false
    }
  }, [])

  const applyPtz = useCallback(
    (axis: PtzAxis, value: number) => {
      setPtzValues((prev) => ({ ...prev, [axis]: value }))
      ptzPendingRef.current = { ...(ptzPendingRef.current || {}), [axis]: value }
      void drainPtz()
    },
    [drainPtz]
  )

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop())
      streamRef.current = null
    }
    trackRef.current = null
    ptzPendingRef.current = null
  }, [])

  const startCamera = useCallback(async () => {
    const size = { width: { ideal: 1280 }, height: { ideal: 960 } }

    let stream: MediaStream
    try {
      // Asking for pan/tilt/zoom up front is what makes the browser grant the
      // PTZ camera permission; cameras without it still satisfy this request.
      stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: { ...size, pan: true, tilt: true, zoom: true } as any,
      })
    } catch {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: size,
        })
      } catch {
        setError('Could not access camera. Please check your permissions.')
        return
      }
    }

    streamRef.current = stream
    const track = stream.getVideoTracks()[0] || null
    trackRef.current = track

    if (track) {
      const ranges = readPtzRanges(track)
      const values = readPtzValues(track, ranges)
      ptzDefaultsRef.current = values
      setPtzRanges(ranges)
      setPtzValues(values)
    }

    if (videoRef.current) {
      videoRef.current.srcObject = stream
      videoRef.current.play().catch(() => {
        /* autoplay can be interrupted by a fast close — the element retries */
      })
    }
  }, [])

  useEffect(() => {
    if (open) {
      setError('')
      setZoom(MIN_DIGITAL_ZOOM)
      setCenter(DEFAULT_CENTER)
      setPtzRanges({})
      setPtzValues({})
      void startCamera()
    } else {
      stopCamera()
    }
    return () => stopCamera()
  }, [open, startCamera, stopCamera])

  // ─── Digital pan/zoom interaction ──────────────────────────────────────

  const zoomTo = useCallback(
    (nextZoom: number, anchor?: Center) => {
      const clampedZoom = clamp(nextZoom, MIN_DIGITAL_ZOOM, MAX_DIGITAL_ZOOM)
      setZoom(clampedZoom)

      if (!anchor) {
        setCenter(clampCenter(center, clampedZoom))
        return
      }

      // Keep whatever sits under the pointer pinned while zooming.
      const pointX = center.x + (anchor.x - 0.5) / zoom
      const pointY = center.y + (anchor.y - 0.5) / zoom
      setCenter(
        clampCenter(
          {
            x: pointX - (anchor.x - 0.5) / clampedZoom,
            y: pointY - (anchor.y - 0.5) / clampedZoom,
          },
          clampedZoom
        )
      )
    },
    [center, zoom]
  )

  const resetView = useCallback(() => {
    setZoom(MIN_DIGITAL_ZOOM)
    setCenter(DEFAULT_CENTER)
    PTZ_AXES.forEach((axis) => {
      const value = ptzDefaultsRef.current[axis]
      if (typeof value === 'number') applyPtz(axis, value)
    })
  }, [applyPtz])

  // React registers wheel handlers passively on the root, so the listener is
  // attached directly to keep the dialog from scrolling behind the zoom.
  useEffect(() => {
    const frame = frameRef.current
    if (!open || error || !frame) return

    const onWheel = (e: WheelEvent) => {
      e.preventDefault()
      const rect = frame.getBoundingClientRect()
      zoomTo(zoom * (e.deltaY < 0 ? 1.15 : 1 / 1.15), {
        x: (e.clientX - rect.left) / rect.width,
        y: (e.clientY - rect.top) / rect.height,
      })
    }

    frame.addEventListener('wheel', onWheel, { passive: false })
    return () => frame.removeEventListener('wheel', onWheel)
  }, [open, error, zoom, zoomTo])

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (zoom <= MIN_DIGITAL_ZOOM) return
    e.currentTarget.setPointerCapture(e.pointerId)
    dragOriginRef.current = { x: e.clientX, y: e.clientY, center }
    setDragging(true)
  }

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const origin = dragOriginRef.current
    const rect = frameRef.current?.getBoundingClientRect()
    if (!origin || !rect) return
    // Dragging right should reveal what is to the left, so the crop centre
    // moves against the pointer, scaled down by the current zoom.
    setCenter(
      clampCenter(
        {
          x: origin.center.x - (e.clientX - origin.x) / rect.width / zoom,
          y: origin.center.y - (e.clientY - origin.y) / rect.height / zoom,
        },
        zoom
      )
    )
  }

  const endDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragOriginRef.current) return
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId)
    }
    dragOriginRef.current = null
    setDragging(false)
  }

  // ─── Capture ───────────────────────────────────────────────────────────

  const handleSnap = () => {
    const video = videoRef.current
    const canvas = canvasRef.current
    if (!video || !canvas) return

    const vw = video.videoWidth
    const vh = video.videoHeight
    if (!vw || !vh) return

    // The preview is object-cover inside a 4:3 frame, so the source is first
    // reduced to the 4:3 region the user can actually see, then to the crop
    // their zoom/pan selected out of it.
    const videoAspect = vw / vh
    const baseW = videoAspect > CONTAINER_ASPECT ? vh * CONTAINER_ASPECT : vw
    const baseH = videoAspect > CONTAINER_ASPECT ? vh : vw / CONTAINER_ASPECT
    const baseX = (vw - baseW) / 2
    const baseY = (vh - baseH) / 2

    const safeCenter = clampCenter(center, zoom)
    const cropW = baseW / zoom
    const cropH = baseH / zoom
    const sx = baseX + safeCenter.x * baseW - cropW / 2
    const sy = baseY + safeCenter.y * baseH - cropH / 2

    canvas.width = OUTPUT_WIDTH
    canvas.height = OUTPUT_HEIGHT
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    ctx.drawImage(
      video,
      sx,
      sy,
      cropW,
      cropH,
      0,
      0,
      OUTPUT_WIDTH,
      OUTPUT_HEIGHT
    )
    onSnap(canvas.toDataURL('image/jpeg'))
    onOpenChange(false)
  }

  // ─── Render ────────────────────────────────────────────────────────────

  const translate = { x: (0.5 - center.x) * 100, y: (0.5 - center.y) * 100 }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[640px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Camera className="h-5 w-5" />
            Take Photo
            {hasPtz && (
              <Badge variant="secondary" className="ml-1">
                PTZ camera
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            Position the subject inside the circle, then click "Capture".
            Scroll or use the zoom slider to zoom, and drag the preview to pan.
          </DialogDescription>
        </DialogHeader>

        <div
          ref={frameRef}
          className={`relative rounded-lg overflow-hidden bg-black aspect-[4/3] touch-none ${
            error
              ? ''
              : zoom > MIN_DIGITAL_ZOOM
                ? dragging
                  ? 'cursor-grabbing'
                  : 'cursor-grab'
                : 'cursor-default'
          }`}
          onPointerDown={error ? undefined : handlePointerDown}
          onPointerMove={error ? undefined : handlePointerMove}
          onPointerUp={error ? undefined : endDrag}
          onPointerCancel={error ? undefined : endDrag}
        >
          {error ? (
            <div className="absolute inset-0 flex items-center justify-center text-white text-sm">
              {error}
            </div>
          ) : (
            <>
              <video
                ref={videoRef}
                className="w-full h-full object-cover select-none"
                style={{
                  transform: `scale(${zoom}) translate(${translate.x}%, ${translate.y}%)`,
                }}
                autoPlay
                playsInline
                muted
              />
              {/* Circle overlay guide */}
              <svg
                className="absolute inset-0 w-full h-full pointer-events-none"
                viewBox="0 0 640 480"
              >
                <defs>
                  <mask id="circle-mask">
                    <rect x="0" y="0" width="640" height="480" fill="white" />
                    <circle cx="320" cy="240" r="200" fill="black" />
                  </mask>
                </defs>
                <rect
                  x="0"
                  y="0"
                  width="640"
                  height="480"
                  fill="rgba(0,0,0,0.4)"
                  mask="url(#circle-mask)"
                />
              </svg>
              {zoom > MIN_DIGITAL_ZOOM && (
                <div className="absolute bottom-2 left-2 flex items-center gap-1 rounded bg-black/60 px-2 py-1 text-xs text-white pointer-events-none">
                  <Move className="h-3 w-3" />
                  {zoom.toFixed(1)}x
                </div>
              )}
            </>
          )}
          <canvas ref={canvasRef} className="hidden" />
        </div>

        {!error && (
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-8 w-8 shrink-0"
                onClick={() => zoomTo(zoom - 0.25)}
                disabled={zoom <= MIN_DIGITAL_ZOOM}
                aria-label="Zoom out"
              >
                <ZoomOut className="h-4 w-4" />
              </Button>
              <input
                type="range"
                min={MIN_DIGITAL_ZOOM}
                max={MAX_DIGITAL_ZOOM}
                step={0.05}
                value={zoom}
                onChange={(e) => zoomTo(parseFloat(e.target.value))}
                className="h-2 w-full cursor-pointer accent-primary"
                aria-label="Digital zoom"
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-8 w-8 shrink-0"
                onClick={() => zoomTo(zoom + 0.25)}
                disabled={zoom >= MAX_DIGITAL_ZOOM}
                aria-label="Zoom in"
              >
                <ZoomIn className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="shrink-0"
                onClick={resetView}
              >
                <RotateCcw className="mr-2 h-3.5 w-3.5" />
                Reset
              </Button>
            </div>

            {hasPtz && (
              <div className="space-y-2 rounded-md border p-3">
                <p className="text-xs font-medium text-muted-foreground">
                  Camera controls
                </p>
                {PTZ_AXES.map((axis) => {
                  const range = ptzRanges[axis]
                  if (!range) return null
                  return (
                    <div key={axis} className="flex items-center gap-3">
                      <span className="w-24 shrink-0 text-xs text-muted-foreground">
                        {AXIS_LABEL[axis]}
                      </span>
                      <input
                        type="range"
                        min={range.min}
                        max={range.max}
                        step={range.step}
                        value={ptzValues[axis] ?? range.min}
                        onChange={(e) =>
                          applyPtz(axis, parseFloat(e.target.value))
                        }
                        className="h-2 w-full cursor-pointer accent-primary"
                        aria-label={AXIS_LABEL[axis]}
                      />
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSnap} disabled={!!error}>
            <Camera className="mr-2 h-4 w-4" />
            Capture
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
